import {
    Fragment,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type CSSProperties,
    type FC,
    type KeyboardEvent as TecladoReact,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { useCelular } from '../hooks/useCelular';
import './MenuContexto.css';

export interface ItemMenuContexto {
    id: string;
    rotulo: string;
    icone?: ReactNode;
    onSelecionar: () => void;
    desabilitado?: boolean;
    /** Filete antes do item: separa blocos do menu. O menu da conversa usa `tituloAntes`. */
    separadorAntes?: boolean;
    /**
     * Título de seção antes do item ("Caixa", "Atendimento", "Negócio"). É só rótulo: não é item, o teclado
     * não para nele. Quem monta a lista põe o título no primeiro item VISÍVEL da seção, então seção sem item
     * some junto com o título.
     */
    tituloAntes?: string;
    /**
     * Submenu: item com filhos não executa nada, ele abre a lista. No computador a lista abre ao lado
     * (seta para a direita entra, para a esquerda volta); no celular o toque troca o conteúdo da folha
     * e um "Voltar" traz de volta. Um nível só — o menu não tem submenu de submenu.
     */
    itens?: ItemMenuContexto[];
    /** Texto discreto à direita do rótulo (o horário que um prazo resulta, por exemplo). */
    detalhe?: string;
    /** Lê em vermelho: o que não tem volta fácil (excluir). Mesma leitura da lixeira da listagem. */
    perigoso?: boolean;
}

interface MenuContextoProps {
    itens: ItemMenuContexto[];
    /** Onde o botão direito (ou o toque longo) aconteceu, em coordenadas da janela. */
    posicao: { x: number; y: number };
    /** Cola a borda DIREITA do menu em `posicao.x` (o kebab abre para a esquerda do botão). */
    alinharAoFim?: boolean;
    /** Título da folha de celular (o nome da conversa). No desktop não aparece. */
    titulo?: string;
    /**
     * Elemento de onde copiar as variáveis `--ch-*`: o menu mora no `body` (para não ser cortado pelo
     * `overflow` da lista) e lá elas não existem. Mesmo expediente do portal do Select.
     */
    herdarDe: Element | null;
    aoFechar: () => void;
    /**
     * A abertura veio de um toque longo (o dedo ainda está na tela). Só nesse caso o menu protege o item do
     * clique do levantar do dedo. Com mouse não há dedo a levantar, e armar a proteção ali engolia o primeiro
     * clique do usuário num item.
     */
    abertoPorToque?: boolean;
}

const TOKENS = ['ink', 'muted', 'faint', 'line', 'line-soft', 'surface', 'active', 'blue', 'blue-wash', 'navy', 'danger'];

const MARGEM = 8;

/**
 * Copia os tokens da tela que abriu o menu. **Valor vazio não entra**: fora do Chat os `--ch-*` não
 * existem (a Pipeline tem os seus, `--lk-*`), e escrever a string vazia no inline apagaria o piso que
 * o `MenuContexto.css` declara — token inexistente pinta de branco no branco.
 */
const lerTokens = (origem: Element | null): CSSProperties => {
    const saida: Record<string, string> = {};
    if (origem) {
        const estilo = getComputedStyle(origem);
        TOKENS.forEach(t => {
            const valor = estilo.getPropertyValue(`--ch-${t}`).trim();
            if (valor) saida[`--ch-${t}`] = valor;
        });
    }
    return saida as CSSProperties;
};

/**
 * Menu de contexto: botão direito no computador, folha presa ao rodapé no celular.
 * É o mesmo componente nas duas cascas, não duas implementações (chat-menu-e-atalhos.html §5).
 *
 * Fecha com Esc, clique fora e rolagem; ↑ ↓ andam, Enter escolhe.
 */
const MenuContexto: FC<MenuContextoProps> = ({ itens, posicao, alinharAoFim, titulo, herdarDe, aoFechar, abertoPorToque }) => {
    const celular = useCelular();
    const menuRef = useRef<HTMLDivElement>(null);
    const subRef = useRef<HTMLDivElement>(null);
    const botoesRef = useRef<(HTMLButtonElement | null)[]>([]);
    const [marcado, setMarcado] = useState(-1);
    /* Submenu: qual item do menu está aberto, qual filho está marcado, e se o teclado já entrou nele. */
    const [subAberto, setSubAberto] = useState<number | null>(null);
    const [marcadoSub, setMarcadoSub] = useState(-1);
    const [noSub, setNoSub] = useState(false);
    const [subPos, setSubPos] = useState<CSSProperties | null>(null);
    const [flutua, setFlutua] = useState<CSSProperties | null>(null);
    const focoAnterior = useRef<Element | null>(document.activeElement);

    const pai = subAberto !== null ? itens[subAberto] : undefined;
    const filhos = pai?.itens ?? [];
    /* Nível em que o teclado está: o submenu só conta depois que a pessoa entrou nele. */
    const nivelSub = noSub && !!pai;
    const listaAtiva = nivelSub ? filhos : itens;
    const marcadoAtivo = nivelSub ? marcadoSub : marcado;
    const setMarcadoAtivo = nivelSub ? setMarcadoSub : setMarcado;

    const selecionaveis = listaAtiva.map((it, i) => (it.desabilitado ? -1 : i)).filter(i => i >= 0);

    /* O token do tema chega inline: a folha de celular e o menu de computador leem o mesmo. */
    const [tema] = useState<CSSProperties>(() => lerTokens(herdarDe));

    /* O dedo que fez o toque longo ainda está na tela quando a folha abre; ao levantar, o navegador pode
       soltar um clique sobre a cortina e fechar tudo na hora. Cliques logo depois de abrir não valem. */
    const abertoEm = useRef(Date.now());
    const levantouEm = useRef(0);
    const recemAberto = () => Date.now() - abertoEm.current < 400 || Date.now() - levantouEm.current < 400;

    /* O toque longo abre o menu com o dedo AINDA na tela: o 400ms de cima conta da abertura, mas a pessoa pode
       segurar mais que isso, e o clique que o navegador solta ao levantar cairia num item (a folha do celular
       ocupa o rodapé, onde o dedo costuma estar) — foi assim que uma conversa se arquivou sozinha. Então o
       primeiro levantar do dedo depois da abertura também abre uma janela de 400ms em que clique não vale.
       Só o primeiro: o toque de verdade num item vem depois e passa. */
    useEffect(() => {
        if (!abertoPorToque) return;
        const marcar = () => { levantouEm.current = Date.now(); };
        const opcoes = { once: true, capture: true } as const;
        const eventos = ['touchend', 'touchcancel', 'pointerup'] as const;
        eventos.forEach(e => window.addEventListener(e, marcar, opcoes));
        return () => eventos.forEach(e => window.removeEventListener(e, marcar, opcoes));
    }, [abertoPorToque]);

    /* Mede o menu já desenhado (escondido) e decide o lado: vira para a esquerda quando não cabe à
       direita e para cima quando não cabe embaixo, sempre dentro da janela. */
    useLayoutEffect(() => {
        if (celular) { setFlutua(null); return; }
        const caixa = menuRef.current;
        if (!caixa) return;
        const largura = caixa.offsetWidth;
        const altura = caixa.offsetHeight;
        let left = alinharAoFim ? posicao.x - largura : posicao.x;
        if (left + largura > window.innerWidth - MARGEM) left = posicao.x - largura;
        left = Math.max(MARGEM, Math.min(left, window.innerWidth - largura - MARGEM));
        let top = posicao.y;
        if (top + altura > window.innerHeight - MARGEM) top = posicao.y - altura;
        top = Math.max(MARGEM, Math.min(top, window.innerHeight - altura - MARGEM));
        setFlutua({ left, top });
    }, [celular, posicao.x, posicao.y, alinharAoFim, itens.length]);

    /* O submenu nasce ao lado do item que o abriu, medido já desenhado (escondido), como o menu:
       vira para a esquerda quando não cabe à direita e sobe quando não cabe embaixo. */
    useLayoutEffect(() => {
        if (celular || subAberto === null) { setSubPos(null); return; }
        const caixa = subRef.current;
        const menu = menuRef.current;
        const botao = botoesRef.current[subAberto];
        if (!caixa || !menu || !botao) return;
        const m = menu.getBoundingClientRect();
        const b = botao.getBoundingClientRect();
        const largura = caixa.offsetWidth;
        const altura = caixa.offsetHeight;
        let left = m.right + 2;
        if (left + largura > window.innerWidth - MARGEM) left = m.left - largura - 2;
        left = Math.max(MARGEM, left);
        let top = b.top - 5;
        if (top + altura > window.innerHeight - MARGEM) top = window.innerHeight - altura - MARGEM;
        top = Math.max(MARGEM, top);
        setSubPos({ left, top });
    }, [celular, subAberto, flutua]);

    /* Devolver o foco para quem abriu o menu, ao fechar. */
    useEffect(() => {
        const anterior = focoAnterior.current;
        return () => {
            if (anterior instanceof HTMLElement && document.contains(anterior)) {
                anterior.focus({ preventScroll: true });
            }
        };
    }, []);

    /* O foco só pode ir para o menu DEPOIS de ele estar visível. No computador ele nasce com
       `visibility: hidden` enquanto o `useLayoutEffect` não mediu e decidiu o lado, e o navegador
       ignora `.focus()` em elemento escondido — em silêncio. Focar cedo demais deixava o foco na
       linha da lista: Esc não fechava e as setas não andavam (medido). No celular não há medida,
       então `flutua` é nulo de propósito e o foco vai na primeira passada. */
    const jaFocou = useRef(false);
    useEffect(() => {
        if (jaFocou.current || (!celular && !flutua)) return;
        menuRef.current?.focus({ preventScroll: true });
        jaFocou.current = true;
    }, [celular, flutua]);

    useEffect(() => {
        const fora = (e: MouseEvent) => {
            const alvo = e.target as Node;
            if (!recemAberto() && !menuRef.current?.contains(alvo) && !subRef.current?.contains(alvo)) aoFechar();
        };
        /* Rolagem fecha (a lista anda por baixo e o menu ficaria no ar), mas não a do próprio menu
           — a folha do celular rola quando a janela é baixa. Captura: `scroll` não borbulha. */
        const rolou = (e: Event) => {
            const alvo = e.target as Node;
            if (menuRef.current?.contains(alvo) || subRef.current?.contains(alvo)) return;
            aoFechar();
        };
        document.addEventListener('mousedown', fora);
        window.addEventListener('scroll', rolou, true);
        window.addEventListener('resize', aoFechar);
        return () => {
            document.removeEventListener('mousedown', fora);
            window.removeEventListener('scroll', rolou, true);
            window.removeEventListener('resize', aoFechar);
        };
    }, [aoFechar]);

    const escolher = (item: ItemMenuContexto) => {
        if (item.desabilitado) return;
        aoFechar();
        item.onSelecionar();
    };

    /* Abre o submenu de um item. `pelaTecla` entra nele e marca o primeiro filho; pelo mouse ele só
       aparece (o teclado continua no menu). No celular o toque sempre entra: o conteúdo da folha troca. */
    const abrirSub = (indice: number, entrar: boolean) => {
        const alvo = itens[indice];
        if (!alvo?.itens?.length || alvo.desabilitado) return;
        setSubAberto(indice);
        setNoSub(entrar);
        setMarcadoSub(entrar ? alvo.itens.findIndex(f => !f.desabilitado) : -1);
    };

    const fecharSub = () => {
        setSubAberto(null);
        setNoSub(false);
        setMarcadoSub(-1);
    };

    const aoClicarItem = (item: ItemMenuContexto, i: number) => {
        if (item.itens?.length) {
            if (subAberto === i && !celular) fecharSub();
            else abrirSub(i, true);
            return;
        }
        escolher(item);
    };

    const andar = (passo: number) => {
        if (!selecionaveis.length) return;
        const posicaoAtual = selecionaveis.indexOf(marcadoAtivo);
        const proxima = posicaoAtual < 0
            ? (passo > 0 ? 0 : selecionaveis.length - 1)
            : (posicaoAtual + passo + selecionaveis.length) % selecionaveis.length;
        setMarcadoAtivo(selecionaveis[proxima]);
    };

    const noTeclado = (e: TecladoReact) => {
        switch (e.key) {
            case 'Escape':
                e.preventDefault();
                /* Esc volta um nível antes de fechar tudo. */
                if (subAberto !== null) fecharSub();
                else aoFechar();
                break;
            case 'ArrowDown':
                e.preventDefault();
                andar(1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                andar(-1);
                break;
            case 'ArrowRight':
                if (!nivelSub && marcado >= 0 && itens[marcado]?.itens?.length) {
                    e.preventDefault();
                    abrirSub(marcado, true);
                }
                break;
            case 'ArrowLeft':
                if (subAberto !== null) {
                    e.preventDefault();
                    fecharSub();
                }
                break;
            case 'Home':
                e.preventDefault();
                if (selecionaveis.length) setMarcadoAtivo(selecionaveis[0]);
                break;
            case 'End':
                e.preventDefault();
                if (selecionaveis.length) setMarcadoAtivo(selecionaveis[selecionaveis.length - 1]);
                break;
            case 'Enter':
            case ' ':
                if (marcadoAtivo >= 0) {
                    e.preventDefault();
                    if (nivelSub) escolher(filhos[marcadoAtivo]);
                    else aoClicarItem(itens[marcadoAtivo], marcadoAtivo);
                }
                break;
            case 'Tab':
                e.preventDefault();
                break;
        }
    };

    const renderItem = (item: ItemMenuContexto, i: number, nivel: 'menu' | 'sub') => {
        const temFilhos = !!item.itens?.length;
        const ativo = nivel === 'menu' ? i === marcado : i === marcadoSub;
        return (
            <Fragment key={item.id}>
            {item.tituloAntes && <div className="mc-secao" role="presentation">{item.tituloAntes}</div>}
            <button
                ref={nivel === 'menu' ? el => { botoesRef.current[i] = el; } : undefined}
                type="button"
                role="menuitem"
                tabIndex={-1}
                disabled={item.desabilitado}
                aria-haspopup={temFilhos ? 'menu' : undefined}
                aria-expanded={temFilhos ? subAberto === i : undefined}
                className={`mc-item${ativo ? ' is-marcado' : ''}${item.separadorAntes ? ' tem-filete' : ''}${temFilhos && subAberto === i ? ' is-aberto' : ''}${item.perigoso ? ' is-perigo' : ''}`}
                onClick={() => {
                    if (recemAberto()) return;
                    if (nivel === 'sub') escolher(item); else aoClicarItem(item, i);
                }}
                onMouseMove={() => {
                    if (item.desabilitado) return;
                    if (nivel === 'sub') {
                        if (marcadoSub !== i || !noSub) { setMarcadoSub(i); setNoSub(true); }
                        return;
                    }
                    if (marcado !== i || noSub) { setMarcado(i); setNoSub(false); }
                    /* No computador o submenu abre ao passar; em outro item, fecha. */
                    if (!celular) {
                        if (temFilhos && subAberto !== i) abrirSub(i, false);
                        else if (!temFilhos && subAberto !== null) fecharSub();
                    }
                }}
            >
                {item.icone}
                <span className="mc-rotulo">{item.rotulo}</span>
                {item.detalhe && <span className="mc-detalhe">{item.detalhe}</span>}
                {temFilhos && <LuChevronRight size={14} className="mc-seta" />}
            </button>
            </Fragment>
        );
    };

    const lista = itens.map((item, i) => renderItem(item, i, 'menu'));

    if (celular) {
        /* Com submenu aberto a folha mostra os filhos, com um "Voltar" no alto. */
        const naSub = !!pai;
        return createPortal(
            <div className="mc-cortina" style={tema} onMouseDown={e => e.target === e.currentTarget && !recemAberto() && aoFechar()}>
                <div
                    ref={menuRef}
                    className="mc-folha"
                    role="menu"
                    tabIndex={-1}
                    aria-label={naSub ? pai!.rotulo : (titulo ?? 'Ações da conversa')}
                    onKeyDown={noTeclado}
                >
                    {naSub ? (
                        <button type="button" className="mc-voltar" onClick={() => !recemAberto() && fecharSub()}>
                            <LuChevronLeft size={16} />
                            <span>{pai!.rotulo}</span>
                        </button>
                    ) : (
                        titulo && <div className="mc-folha-titulo">{titulo}</div>
                    )}
                    {naSub ? filhos.map((f, i) => renderItem(f, i, 'sub')) : lista}
                    <button type="button" className="mc-item mc-cancelar tem-filete" onClick={() => !recemAberto() && aoFechar()}>
                        <span>Cancelar</span>
                    </button>
                </div>
            </div>,
            document.body,
        );
    }

    return createPortal(
        <>
            <div
                ref={menuRef}
                className="mc-menu"
                role="menu"
                tabIndex={-1}
                aria-label={titulo ?? 'Ações da conversa'}
                style={{ ...tema, ...(flutua ?? { left: 0, top: 0, visibility: 'hidden' }) }}
                onKeyDown={noTeclado}
                onContextMenu={e => e.preventDefault()}
            >
                {lista}
            </div>
            {pai && (
                <div
                    ref={subRef}
                    className="mc-menu mc-sub"
                    role="menu"
                    aria-label={pai.rotulo}
                    style={{ ...tema, ...(subPos ?? { left: 0, top: 0, visibility: 'hidden' }) }}
                    onContextMenu={e => e.preventDefault()}
                >
                    {filhos.map((f, i) => renderItem(f, i, 'sub'))}
                </div>
            )}
        </>,
        document.body,
    );
};

export default MenuContexto;
